/**
 * =====================================================================================
 * MÓDULO VUEX: THEME (Gestión de Temas)
 * =====================================================================================
 *
 * Este módulo gestiona el tema visual de la aplicación (claro, oscuro, variantes).
 * Persiste la preferencia del usuario en localStorage para mantenerla entre sesiones.
 *
 * CARACTERÍSTICAS:
 * - Tema claro y oscuro
 * - Múltiples variantes de color (azul, indigo, verde, etc.)
 * - Persistencia en localStorage
 * - Detección de preferencia del sistema operativo
 *
 * =====================================================================================
 */

// Clave para localStorage
const STORAGE_KEY = 'dte-theme-preference'

// Temas disponibles
export const THEMES = {
  LIGHT: 'lightTheme',
  DARK: 'darkTheme'
}

// Variantes de color disponibles
export const COLOR_VARIANTS = {
  DEFAULT: 'default',      // Azul corporativo
  INDIGO: 'indigo',        // Indigo/violeta
  TEAL: 'teal',            // Verde azulado
  GREEN: 'green',          // Verde
  ORANGE: 'orange',        // Naranja
  PURPLE: 'purple'         // Morado
}

/**
 * Obtiene la preferencia guardada del localStorage
 * @returns {Object} Preferencias de tema guardadas
 */
const getSavedPreference = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.warn('Error al leer preferencias de tema:', e)
  }
  return null
}

/**
 * Guarda la preferencia en localStorage
 * @param {Object} preference - Preferencias a guardar
 */
const savePreference = (preference) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preference))
  } catch (e) {
    console.warn('Error al guardar preferencias de tema:', e)
  }
}

/**
 * Detecta si el sistema operativo prefiere tema oscuro
 * @returns {boolean} true si el SO prefiere tema oscuro
 */
const prefersDarkMode = () => {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export default {
  namespaced: true,

  // =====================================================================================
  // STATE
  // =====================================================================================
  state: () => {
    // Intentar cargar preferencias guardadas
    const saved = getSavedPreference()

    return {
      // Tema actual: 'lightTheme' o 'darkTheme'
      currentTheme: saved?.theme || THEMES.LIGHT,

      // Variante de color actual
      colorVariant: saved?.variant || COLOR_VARIANTS.DEFAULT,

      // Si debe seguir la preferencia del sistema
      followSystem: saved?.followSystem ?? false,

      // Temas disponibles para el selector
      availableThemes: [
        { value: THEMES.LIGHT, title: 'Claro', icon: 'mdi-white-balance-sunny' },
        { value: THEMES.DARK, title: 'Oscuro', icon: 'mdi-moon-waning-crescent' }
      ],

      // Variantes de color disponibles
      availableVariants: [
        { value: 'default', title: 'Azul Corporativo', color: '#1565C0' },
        { value: 'indigo', title: 'Índigo', color: '#3949AB' },
        { value: 'teal', title: 'Verde Azulado', color: '#00897B' },
        { value: 'green', title: 'Verde', color: '#43A047' },
        { value: 'orange', title: 'Naranja', color: '#EF6C00' },
        { value: 'purple', title: 'Morado', color: '#7B1FA2' }
      ]
    }
  },

  // =====================================================================================
  // GETTERS
  // =====================================================================================
  getters: {
    /**
     * Retorna el tema actual
     */
    currentTheme: (state) => state.currentTheme,

    /**
     * Retorna true si el tema actual es oscuro
     */
    isDarkTheme: (state) => state.currentTheme === THEMES.DARK,

    /**
     * Retorna la variante de color actual
     */
    colorVariant: (state) => state.colorVariant,

    /**
     * Retorna si sigue la preferencia del sistema
     */
    followSystem: (state) => state.followSystem,

    /**
     * Retorna los temas disponibles
     */
    availableThemes: (state) => state.availableThemes,

    /**
     * Retorna las variantes de color disponibles
     */
    availableVariants: (state) => state.availableVariants,

    /**
     * Retorna el nombre del tema actual para mostrar
     */
    currentThemeName: (state) => {
      const theme = state.availableThemes.find(t => t.value === state.currentTheme)
      return theme?.title || 'Claro'
    },

    /**
     * Retorna la información de la variante actual
     */
    currentVariantInfo: (state) => {
      return state.availableVariants.find(v => v.value === state.colorVariant) || state.availableVariants[0]
    }
  },

  // =====================================================================================
  // MUTATIONS
  // =====================================================================================
  mutations: {
    /**
     * Establece el tema actual
     */
    SET_THEME(state, theme) {
      state.currentTheme = theme
    },

    /**
     * Establece la variante de color
     */
    SET_COLOR_VARIANT(state, variant) {
      state.colorVariant = variant
    },

    /**
     * Establece si seguir la preferencia del sistema
     */
    SET_FOLLOW_SYSTEM(state, follow) {
      state.followSystem = follow
    }
  },

  // =====================================================================================
  // ACTIONS
  // =====================================================================================
  actions: {
    /**
     * Inicializa el tema al cargar la aplicación
     * Detecta preferencia del sistema si está configurado
     */
    initTheme({ state, commit, dispatch }) {
      // Si debe seguir el sistema, detectar preferencia
      if (state.followSystem) {
        const systemDark = prefersDarkMode()
        commit('SET_THEME', systemDark ? THEMES.DARK : THEMES.LIGHT)

        // Escuchar cambios en la preferencia del sistema
        if (window.matchMedia) {
          window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            if (state.followSystem) {
              dispatch('setTheme', e.matches ? THEMES.DARK : THEMES.LIGHT)
            }
          })
        }
      }

      // Aplicar el tema actual
      dispatch('applyTheme')
    },

    /**
     * Cambia el tema y lo persiste
     * @param {string} theme - 'lightTheme' o 'darkTheme'
     */
    setTheme({ commit, state, dispatch }, theme) {
      commit('SET_THEME', theme)
      dispatch('applyTheme')
      dispatch('savePreferences')
    },

    /**
     * Alterna entre tema claro y oscuro
     */
    toggleTheme({ state, dispatch }) {
      const newTheme = state.currentTheme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT
      dispatch('setTheme', newTheme)
    },

    /**
     * Cambia la variante de color
     * @param {string} variant - Código de la variante
     */
    setColorVariant({ commit, dispatch }, variant) {
      commit('SET_COLOR_VARIANT', variant)
      dispatch('applyTheme')
      dispatch('savePreferences')
    },

    /**
     * Activa/desactiva seguir la preferencia del sistema
     */
    setFollowSystem({ commit, dispatch }, follow) {
      commit('SET_FOLLOW_SYSTEM', follow)
      if (follow) {
        const systemDark = prefersDarkMode()
        dispatch('setTheme', systemDark ? THEMES.DARK : THEMES.LIGHT)
      }
      dispatch('savePreferences')
    },

    /**
     * Aplica el tema actual a Vuetify
     * Esta acción debe ser llamada después de que Vuetify esté disponible
     */
    applyTheme({ state }) {
      // El tema se aplica a través de Vuetify en el componente
      // Este action sirve como punto de sincronización
      return {
        theme: state.currentTheme,
        variant: state.colorVariant
      }
    },

    /**
     * Guarda las preferencias actuales en localStorage
     */
    savePreferences({ state }) {
      savePreference({
        theme: state.currentTheme,
        variant: state.colorVariant,
        followSystem: state.followSystem
      })
    }
  }
}
